/**
 * PhotoAlbumSection — collapsible album section on the vehicle dossier.
 *
 * Placement: inserted between ModificationsSection and the <nav> sub-nav
 * on VehicleDetail, gated by VITE_PHOTOS_ENABLED.
 *
 * Layout:
 *   - SpecBlock wrapper, marginTop SPACE.lg
 *   - Header row: <h2>Photos</h2> + count Pill + "Add photos" Button (disclosure toggle)
 *   - CSS grid: repeat(auto-fill, minmax(150px, 1fr)), gap SPACE.sm
 *
 * States:
 *   - Loading: skeleton grid of 4 square tiles
 *   - Empty: PhotoCapture dropzone as the entire empty state
 *   - Has photos: grid newest-first (capturedAt ?? createdAt desc), pending pinned front
 *
 * Offline: section-level aria-live line when offline + queued items present.
 *
 * Slice 2/3: publish-preview→confirm flow + delete (crypto-shred).
 */

import { Button } from '@/components/Button';
import { Collapsible } from '@/components/Collapsible';
import { F } from '@/design/fonts';
import { SPACE, T } from '@/design/tokens';
import { useAuth } from '@/features/auth/useAuth';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useConfirmPublish, usePublishPreview } from '@/hooks/usePhotoActions';
import { usePhotoQueue } from '@/hooks/usePhotoQueue';
import { type Photo } from '@/hooks/usePhotos';
import { enqueue, type PhotoQueueItem } from '@/lib/photoQueue';
import { ImagePlus } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useCallback, useState } from 'react';
import { PhotoCapture } from './PhotoCapture';
import { PhotoDeleteDialog } from './PhotoDeleteDialog';
import { PhotoLightbox } from './PhotoLightbox';
import { PhotoPublishModal } from './PhotoPublishModal';
import type { DerivativeStatus } from './PhotoTile';
import { PhotoTile, type CommittedPhoto, type PhotoTileItem } from './PhotoTile';

// Skeleton tile for the loading state
function SkeletonTile() {
  return (
    <div
      aria-hidden="true"
      style={{
        aspectRatio: '1 / 1',
        background: T.border,
        borderRadius: 4,
      }}
    />
  );
}

export type ServerPhoto = Photo;

interface PhotoAlbumSectionProps {
  vehicleId: string;
  photos: ServerPhoto[] | undefined;
  loading: boolean;
}

function sortKey(photo: CommittedPhoto): number {
  // Newest first: prefer capturedAt, fall back to createdAt.
  const iso = photo.capturedAt ?? photo.createdAt;
  try {
    return -new Date(iso).getTime();
  } catch {
    return 0;
  }
}

// State for the publish modal.
interface PublishModalState {
  photoId: string;
  derivativeStatus: DerivativeStatus;
}

// State for the delete dialog.
interface DeleteDialogState {
  photoId: string;
}

export function PhotoAlbumSection({ vehicleId, photos, loading }: PhotoAlbumSectionProps) {
  const [lightboxItem, setLightboxItem] = useState<PhotoTileItem | null>(null);
  const [publishModal, setPublishModal] = useState<PublishModalState | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);
  const isOnline = useOnlineStatus();
  const { user } = useAuth();

  const { items: queueItems, drain, retry, discard } = usePhotoQueue(vehicleId);

  const publishPreviewMutation = usePublishPreview(vehicleId);
  const confirmPublishMutation = useConfirmPublish(vehicleId);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!user) return;
      for (const file of files) {
        const bytes = await file.arrayBuffer();
        const clientId = crypto.randomUUID();
        const capturedAt = new Date(file.lastModified).toISOString();
        const previewUrl = URL.createObjectURL(file);

        await enqueue(
          {
            clientId,
            uid: user.uid,
            vehicleId,
            contentType: file.type || 'image/jpeg',
            sizeBytes: file.size,
            capturedAt,
            queuedAt: new Date().toISOString(),
            status: 'pending',
          },
          bytes,
        );

        // Attach a local previewUrl to the item. The queue doesn't persist this
        // but the hook will reconstruct it from bytes on reload.
        // We do this by creating the URL before enqueue so the tile gets it immediately.
        // The hook's refresh will re-attach it via getBytes → createObjectURL.
        // NOTE: We created the URL above; we'll just rely on the hook's refresh
        // to pick it up, so revoke the one we created immediately to avoid leaking.
        URL.revokeObjectURL(previewUrl);
      }
      // Trigger a drain attempt.
      void drain();
    },
    [user, vehicleId, drain],
  );

  // --- Slice 2 action handlers ---

  const handlePublish = useCallback(
    async (photoId: string) => {
      // Fire the publish-preview request, then open the modal in "rendering" state.
      // The modal polls derivativeStatus via the photo list refetch.
      try {
        await publishPreviewMutation.mutateAsync(photoId);
        setPublishModal({ photoId, derivativeStatus: 'rendering' });
      } catch {
        // Error is surfaced via mutation.isError in the tile grid context.
        // Nothing to do here — the modal was never opened.
      }
    },
    [publishPreviewMutation],
  );

  const handleMakePrivate = useCallback(
    async (photoId: string) => {
      await confirmPublishMutation.mutateAsync({ photoId, confirmed: false });
    },
    [confirmPublishMutation],
  );

  const handleDelete = useCallback((photoId: string) => {
    setDeleteDialog({ photoId });
  }, []);

  // Build the committed photo list (only 'committed' status from the server).
  const committedPhotos: CommittedPhoto[] = (photos ?? [])
    .filter((p) => p.status === 'committed')
    .map((p) => {
      // Build CommittedPhoto manually to satisfy exactOptionalPropertyTypes:
      // only include optional fields when they have a value.
      const tile: CommittedPhoto = {
        photoId: p.photoId,
        url: p.url,
        contentType: p.contentType,
        status: 'committed',
        createdAt: p.createdAt,
      };
      if (p.capturedAt !== undefined) tile.capturedAt = p.capturedAt;
      if (p.caption !== undefined) tile.caption = p.caption;
      if (p.publicConfirmed !== undefined) tile.publicConfirmed = p.publicConfirmed;
      if (p.derivativeStatus !== undefined) tile.derivativeStatus = p.derivativeStatus;
      return tile;
    })
    .sort(sortKey);

  // Sync the publish modal's derivativeStatus from the latest photo list so it
  // advances from "rendering" → "ready" or "failed" as the backend worker finishes.
  const publishModalPhoto = publishModal
    ? committedPhotos.find((p) => p.photoId === publishModal.photoId)
    : null;
  const liveDerivativeStatus: DerivativeStatus =
    publishModalPhoto?.derivativeStatus ?? publishModal?.derivativeStatus ?? 'rendering';

  // Queued items — pinned to front, newest-queued first.
  const queuedTiles: PhotoTileItem[] = queueItems.map((q) => ({
    ...q,
    type: 'queued' as const,
  }));

  const allTiles: PhotoTileItem[] = [...queuedTiles, ...committedPhotos];
  const committedCount = committedPhotos.length;
  const pendingCount = queueItems.filter((q) => q.status === 'pending').length;

  const tileActions = {
    onPublish: (photoId: string) => void handlePublish(photoId),
    onMakePrivate: (photoId: string) => void handleMakePrivate(photoId),
    onDelete: handleDelete,
  };

  const gridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: SPACE.sm,
  };

  return (
    <div style={{ marginTop: SPACE.lg }}>
      <Collapsible title="Photos" count={committedCount} defaultOpen={true} id="photos">
        {/* Offline + queued notice */}
        {!isOnline && pendingCount > 0 ? (
          <p
            role="status"
            aria-live="polite"
            style={{
              margin: `0 0 ${SPACE.sm}px`,
              fontSize: 12,
              fontFamily: F.body,
              color: T.orange,
            }}
          >
            {pendingCount} {pendingCount === 1 ? 'photo' : 'photos'} waiting to upload. Will send
            when back online.
          </p>
        ) : null}

        {/* Add photos button — top-right of body */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: SPACE.sm }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // Delegate to the hidden file input inside PhotoCapture.
              const zone = document.querySelector<HTMLElement>(
                '[aria-label="Add photos of your vehicle"]',
              );
              zone?.click();
            }}
          >
            <ImagePlus size={14} aria-hidden="true" />
            Add photos
          </Button>
        </div>

        {loading ? (
          // Loading state: 4 skeleton tiles in the grid
          <div style={gridStyle} role="status" aria-label="Loading photos…">
            {[0, 1, 2, 3].map((i) => (
              <SkeletonTile key={i} />
            ))}
          </div>
        ) : allTiles.length === 0 ? (
          // Empty state: PhotoCapture IS the empty state
          <PhotoCapture onFiles={handleFiles} />
        ) : (
          // Photo grid
          <div style={gridStyle}>
            {allTiles.map((tile) => {
              const key = 'type' in tile && tile.type === 'queued' ? tile.clientId : tile.photoId;
              return (
                <div key={key} style={{ position: 'relative' }}>
                  <PhotoTile item={tile} onOpen={setLightboxItem} actions={tileActions} />
                  {/* Retry / Discard for failed tiles */}
                  {'type' in tile && tile.type === 'queued' && tile.status === 'failed' ? (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: SPACE.xs,
                        background: 'rgba(0,0,0,0.55)',
                        borderRadius: 4,
                        padding: SPACE.xs,
                      }}
                    >
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label="Retry photo upload"
                        onClick={() => void retry((tile as PhotoQueueItem).clientId)}
                      >
                        Retry
                      </Button>
                      <button
                        type="button"
                        aria-label="Discard failed photo upload"
                        onClick={() => void discard((tile as PhotoQueueItem).clientId)}
                        style={{
                          fontSize: 11,
                          fontFamily: F.body,
                          fontWeight: 600,
                          background: 'transparent',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.5)',
                          borderRadius: 2,
                          padding: `${SPACE.xs}px ${SPACE.sm}px`,
                          cursor: 'pointer',
                          minHeight: 44,
                        }}
                      >
                        Discard
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {/* Add more photos — always show the dropzone at the end of the grid */}
            <div style={{ aspectRatio: '1 / 1', display: 'flex', alignItems: 'stretch' }}>
              <PhotoCapture onFiles={handleFiles} />
            </div>
          </div>
        )}
      </Collapsible>

      {/* Upload status announcer — kept outside Collapsible so aria-live
          is never suppressed by the hidden attribute on the collapsed body. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {queueItems
          .filter((q) => q.status === 'inflight')
          .map((q) => `Uploading ${q.caption ?? 'photo'}`)
          .join(', ')}
      </div>

      {/* Lightbox and modals — outside Collapsible so they aren't affected
          by the hidden attribute when the section is collapsed. */}
      <PhotoLightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />

      {/* Publish-preview → confirm modal (Slice 2) */}
      {publishModal ? (
        <PhotoPublishModal
          photoId={publishModal.photoId}
          vehicleId={vehicleId}
          derivativeStatus={liveDerivativeStatus}
          onClose={() => setPublishModal(null)}
          onConfirmed={() => setPublishModal(null)}
        />
      ) : null}

      {/* Delete (crypto-shred) confirmation dialog (Slice 3) */}
      {deleteDialog ? (
        <PhotoDeleteDialog
          photoId={deleteDialog.photoId}
          vehicleId={vehicleId}
          onClose={() => setDeleteDialog(null)}
          onDeleted={() => setDeleteDialog(null)}
        />
      ) : null}
    </div>
  );
}
